import { Component } from '@theme/component';
import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * @typedef {'pagination' | 'infinite_scroll' | 'load_more'} ArticleLoadingMode
 */

class MainBlogComponent extends Component {
  /** @type {IntersectionObserver | undefined} */
  #scrollObserver;

  /** @type {boolean} */
  #loading = false;

  /** @type {{ tag: string, sort: string, query: string }} */
  #toolbarState = { tag: 'all', sort: 'newest', query: '' };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('blog-gallery-toolbar:change', this.#handleToolbarChange);
    this.#setupProgressiveLoading();
  }

  disconnectedCallback() {
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = undefined;
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #handleToolbarChange = (event) => {
    const detail = /** @type {CustomEvent<{ tag?: string; sort?: string; query?: string }>} */ (event).detail;
    if (!detail || typeof detail !== 'object') return;

    const { tag = 'all', sort = 'newest', query = '' } = detail;
    this.#toolbarState = { tag, sort, query };
    this.applyFilters(detail);
  };

  #setupProgressiveLoading() {
    const mode = /** @type {ArticleLoadingMode} */ (this.dataset.articleLoading || 'pagination');
    if (mode !== 'infinite_scroll' && mode !== 'load_more') return;
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return;

    const grid = this.#getGrid();
    if (!grid) return;

    const lastPage = Number.parseInt(grid.dataset.lastPage ?? '1', 10);
    if (Number.isNaN(lastPage) || lastPage <= 1) return;

    const sentinel = this.refs.scrollSentinel;
    const loadMore = this.refs.loadMoreButton;

    if (mode === 'load_more' && loadMore instanceof HTMLButtonElement) {
      loadMore.addEventListener('click', () => this.#loadNextPage());
    }

    if (mode === 'infinite_scroll' && sentinel instanceof HTMLElement) {
      this.#scrollObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && entry.target === sentinel && !this.#loading) {
              this.#loadNextPage();
            }
          }
        },
        { rootMargin: '200px 0px' }
      );
      this.#scrollObserver.observe(sentinel);
    }

    this.#prefetchNextPage();
  }

  #getGrid() {
    return this.querySelector('.main-blog__grid[ref="articlesGrid"], .main-blog__grid');
  }

  #prefetchNextPage() {
    const grid = this.#getGrid();
    const sectionId = this.dataset.sectionId;
    if (!grid || !sectionId) return;

    const current = Number.parseInt(grid.dataset.currentPage ?? '1', 10);
    const last = Number.parseInt(grid.dataset.lastPage ?? '1', 10);
    const next = current + 1;
    if (next > last) return;

    requestIdleCallback(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('page', String(next));
      const useCache = typeof Shopify === 'undefined' || !Shopify.designMode;
      sectionRenderer.getSectionHTML(sectionId, useCache, url).catch(() => {});
    });
  }

  async #loadNextPage() {
    if (this.#loading) return;

    const grid = this.#getGrid();
    const sectionId = this.dataset.sectionId;
    if (!grid || !sectionId) return;

    const current = Number.parseInt(grid.dataset.currentPage ?? '1', 10);
    const last = Number.parseInt(grid.dataset.lastPage ?? '1', 10);
    const nextPage = current + 1;

    if (nextPage > last) {
      this.#finishProgressiveUi();
      return;
    }

    this.#loading = true;
    const loadMore = this.refs.loadMoreButton;
    if (loadMore instanceof HTMLButtonElement) {
      loadMore.disabled = true;
      loadMore.setAttribute('aria-busy', 'true');
    }

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));

    try {
      const useCache = typeof Shopify === 'undefined' || !Shopify.designMode;
      const html = await sectionRenderer.getSectionHTML(sectionId, useCache, url);

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.getElementById(buildSectionSelector(sectionId));
      const freshItems = sectionEl?.querySelectorAll('.main-blog__grid .main-blog__item') ?? [];

      if (!freshItems.length) return;

      for (const node of freshItems) {
        grid.appendChild(node);
      }

      grid.dataset.currentPage = String(nextPage);
      this.applyFilters(this.#toolbarState);

      history.pushState('', '', url.toString());
    } catch (error) {
      console.error('[main-blog]', error);
    } finally {
      this.#loading = false;
      if (loadMore instanceof HTMLButtonElement) {
        loadMore.disabled = false;
        loadMore.removeAttribute('aria-busy');
      }
    }

    const updatedCurrent = Number.parseInt(grid.dataset.currentPage ?? '1', 10);
    const lastPage = Number.parseInt(grid.dataset.lastPage ?? '1', 10);
    if (updatedCurrent >= lastPage) {
      this.#finishProgressiveUi();
    } else {
      this.#prefetchNextPage();
    }
  }

  #finishProgressiveUi() {
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = undefined;

    const sentinel = this.querySelector('.main-blog__scroll-sentinel');
    if (sentinel instanceof HTMLElement) sentinel.hidden = true;

    const btn = this.querySelector('.main-blog__load-more');
    if (btn instanceof HTMLButtonElement) btn.hidden = true;
  }

  /**
   * @param {{ tag?: string; sort?: string; query?: string }} [detail]
   */
  applyFilters(detail = {}) {
    const { tag = 'all', sort = 'newest', query = '' } = detail;
    const isFiltering = tag !== 'all' || sort !== 'newest' || query.trim() !== '';
    this.classList.toggle('main-blog--filtering', isFiltering);

    const grid = this.querySelector('.main-blog__grid');
    const gridItems = grid ? Array.from(grid.querySelectorAll('.main-blog__item[data-tags]')) : [];
    const featuredItem = this.querySelector('.main-blog__featured .main-blog__item[data-tags]');
    const items = featuredItem ? [featuredItem, ...gridItems] : gridItems;
    if (!items.length) return;

    items.forEach((item) => {
      item.setAttribute('data-filtering', 'true');
    });

    requestAnimationFrame(() => {
      setTimeout(() => {
        const normalizedQuery = query.trim().toLowerCase();

        items.forEach((item) => {
          const itemTags = (item.dataset.tags || '').split(',').filter(Boolean);
          const tagMatch = tag === 'all' || itemTags.includes(tag);
          const searchText = (item.dataset.searchText || '').toLowerCase();
          const searchMatch = !normalizedQuery || searchText.includes(normalizedQuery);
          const visible = tagMatch && searchMatch;

          item.setAttribute('data-filtered', visible ? 'false' : 'true');
        });

        this.sortItems(gridItems, sort);

        items.forEach((item) => {
          item.removeAttribute('data-filtering');
        });
      }, 150);
    });
  }

  /**
   * @param {HTMLElement[]} items
   * @param {string} sort
   */
  sortItems(items, sort) {
    const grid = this.querySelector('.main-blog__grid');
    if (!grid) return;

    const visibleItems = items.filter((item) => item.dataset.filtered !== 'true');

    const sorted = [...visibleItems].sort((a, b) => {
      switch (sort) {
        case 'title-asc':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        case 'title-desc':
          return (b.dataset.title || '').localeCompare(a.dataset.title || '');
        case 'oldest':
          return Number(a.dataset.published || 0) - Number(b.dataset.published || 0);
        case 'newest':
        default:
          return Number(b.dataset.published || 0) - Number(a.dataset.published || 0);
      }
    });

    sorted.forEach((item) => grid.appendChild(item));
  }
}

if (!customElements.get('main-blog-component')) {
  customElements.define('main-blog-component', MainBlogComponent);
}
